/**
 * @flow
 */

import Config from 'config';

// Must stay under hot-or-not's WINDOW_SECONDS (30s) or the aggregator ages the
// event out between pings; the gap is headroom for jitter and throttled tabs.
const INTERVAL_MS = 20000;

// Mirrors the edge's &area= allowlist (get_eventid_from_url); anything else is
// dropped at the edge and never counted.
export type Area = 'edp' | 'widget' | 'checkout' | 'queue';

// Keyed by id so several embeds of one event count the fan once.
const timers: { [id: string]: IntervalID } = {};

// Survives pauseAll() so resumeAll() can restart. Area is per id because one
// embed2 can drive an 'edp' and a 'widget' ping on the same page.
const registered: { [id: string]: Area } = {};

let paused: boolean = false;

// Area for a start() that doesn't name one.
let context: Area = 'edp';
export function setContext(surface: Area): void {
  context = surface;
}

// Enable with `localStorage.presenceDebug = '1'` or `window.__presenceDebug = true`.
// Wrapped because storage access throws in a sandboxed iframe.
export function debug(...args: Array<mixed>): void {
  try {
    const enabled =
      (typeof window !== 'undefined' && window.__presenceDebug === true) ||
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('presenceDebug') === '1');
    if (enabled) {
      // eslint-disable-next-line no-console
      console.debug('[presence]', context, ...args);
    }
  }
  catch (e) {
    // sandboxed iframe blocked storage access — stay silent, keep pinging.
  }
}

// The edge only accepts /_presence/{[A-Za-z0-9]+} (get_eventid_from_url) and
// counts page views by a slug's trailing token, so ping with that token.
export function idOf(target: ?string): string {
  const match = String(target || '').match(/-?([0-9A-Za-z]+)$/);
  return match ? match[1] : '';
}

function ping(id: string): void {
  const area: Area = registered[id] || context;
  debug('ping', id, area);
  // Image beacon: no CORS preflight from any embedding origin. The cache-buster
  // gets past intermediaries that ignore the edge's no-store.
  const img = new Image();
  img.src = `${Config.betaCheckout}/_presence/${id}?n=${Date.now()}&area=${area}`;
}

function run(id: string): void {
  if (timers[id]) {
    return;
  }
  // Ping immediately so short views count. A hidden tab isn't a waiting fan.
  ping(id);
  timers[id] = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    ping(id);
  }, INTERVAL_MS);
}

export function start(target: ?string, area?: Area): void {
  const id = idOf(target);
  if (!id) {
    return;
  }
  registered[id] = area || context;
  debug('start', id, registered[id], paused ? '(paused — deferred)' : '');
  if (!paused) {
    run(id);
  }
}

export function stop(target: ?string): void {
  const id = idOf(target);
  if (!id) {
    return;
  }
  debug('stop', id);
  delete registered[id];
  if (timers[id]) {
    clearInterval(timers[id]);
    delete timers[id];
  }
}

// Stops timers but keeps registrations for resumeAll().
export function pauseAll(): void {
  paused = true;
  debug('pauseAll — stopping', Object.keys(timers));
  Object.keys(timers).forEach((id) => {
    clearInterval(timers[id]);
    delete timers[id];
  });
}

export function resumeAll(): void {
  paused = false;
  debug('resumeAll — restarting', Object.keys(registered));
  Object.keys(registered).forEach((id) => {
    run(id);
  });
}



// WEBPACK FOOTER //
// ./src/lib/presence.js